import React, { Suspense, lazy, Fragment, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SET_AXIOS } from './actions/actions';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createBaseAxiosInstance } from './axios-base';
import BuiltInModeWrapper from './components/BuiltInModeWrapper/BuiltInModeWrapper';

import './App.css';
import { Route, Routes, Navigate, useLocation } from 'react-router-dom';
import Loader from 'react-loaders';
import FreemiumReportView from './containers/Reports/ViewReport/Freemium/latest-index';
import NdaSuccess from './containers/UserPages/NdaSuccess/NdaSuccess';
import DocSuccess from './containers/UserPages/DocSuccess/DocSuccess';
import ComingSoon from './components/ComingSoon/ComingSoon';
import Thankyou from './components/Thankyou/Thankyou';
import Layout from './components/Layout/index';
import Profile from './containers/UserPages/Profile/Profile';
import DocDecline from './containers/UserPages/DocDecline/DocDecline';
import TermsOfService from './containers/UserPages/TermsOfService/TermsOfService';
import PrivacyPolicy from './containers/UserPages/PrivacyPolicy/PrivacyPolicy';
import AnnouncementsTable from './containers/Announcements/AnnouncementsTable';
import KnowledgeBaseContentLibrary from './containers/KnowledgeBase/KnowledgeBaseContentLibrary/KnowledgeBaseContentLibrary';
import PocPage from './clubhouse/poc-page/poc-page';
import SubscriptionsPage from './containers/SubscriptionsPage/SubscriptionsPage';
import StandardizationServicesPage from './containers/StandardizationServicesPage/StandardizationServicesPage';
import {
    setExpirationDate,
    setIsOpened,
} from './are-you-still-here/are-you-still-here-slice/are-you-still-here-slice';
import { TokenHelper } from './utils/tokenHelper';
import { useStateSelector } from './store/selectors';
import { setReturnUrl } from './slices/user-summary/user-summary-slice';
import { DataRequestDashboardPage } from './containers/DataRequest/DataRequestDashboardPage/DataRequestDashboardPage';
import { DataAndDocumentTemplatesDashboard } from './containers/Admin/DataRequest/DataAndDocumentTemplatesDashboard/DataAndDocumentTemplatesDashboard';
import { DataAndDocumentRequestsDashboard } from './containers/Admin/DataRequest/DataAndDocumentRequestsDashboard/DataAndDocumentRequestsDashboard';
import { ProjectEditorHost } from './containers/Admin/DataRequest/ProjectEditorHost/ProjectEditorHost';
import DefaultMeta from './components/DefaultMeta/DefaultMeta';
import { RefreshNotificationFlags } from './slices/notification-flags/refresh-notification-flags';
import { NotificationFlagsProvider } from './components/NotificationFlagsProvider/NotificationFlagsProvider';
import AdminMarketplace from './containers/AdminMarketplace/AdminMarketplace';
import TimelineEditor from './containers/AdminMarketplace/TimelinePage/TimelineEditor';
import BuyerMarketplace from './containers/Admin/BuyerMarketplace/BuyerMarketplace';
import LandscapeMapDemoPage from './containers/LandscapeMapContainer/LandscapeMapDemo';
import MarketplaceTemplatePreview from './containers/AdminMarketplace/MarketplaceTemplatePreview/MarketplaceTemplatePreview';
import ConnectMarketplaceDashboard from './containers/ConnectMarketplaceDashboard/ConnectMarketplaceDashboard';
import MeetingLinkRedirect from './containers/MeetingLinkRedirect/MeetingLinkRedirect';
import DocumentSignedFrame from './containers/UserPages/DocumentSignedFrame/DocumentSignedFrame';
import { FetchServiceFocuses } from './slices/service-focuses/thunks/fetch-service-focuses.thunk';
import BuyerTermsOfService from './containers/UserPages/BuyerTermsOfService/BuyerTermsOfService';
const Register = lazy(() => import('./containers/UserPages/Register'));
const Admin = lazy(() => import('./containers/Admin'));
const Dashboard = lazy(() => import('./containers/Dashboard/Dashboard'));
const Login = lazy(() => import('./containers/UserPages/Login/index'));
const Report = lazy(() => import('./containers/Reports/index'));
const Landing = lazy(() => import('./containers/Landing/Landing'));
const FEPage = lazy(() => import('./containers/FreeAndEasy/FEPage'));
const ForgotPwd = lazy(() =>
    import('./containers/UserPages/ForgotPassword/index')
);
const PaidEvaluationReport = lazy(() =>
    import('./containers/Reports/ViewReport/PaidEvaluationReport')
);
const MyPractices = lazy(() => import('./containers/Practices/MyPractices'));
const Practice = lazy(() => import('./containers/Practices/Practice'));
const Associates = lazy(() => import('./containers/Associates/Associates'));
const PracticeList = lazy(() =>
    import('./containers/Practices/Admin/PracticeList')
);
const MyReferals = lazy(() => import('./containers/Referal/MyReferals'));
const PracticeRequests = lazy(() =>
    import('./containers/PracticeRequests/PracticeRequests')
);
const ReferralRequests = lazy(() =>
    import('./containers/ReferralRequests/ReferralRequests')
);
const DetailedReports = lazy(() =>
    import('./containers/DetailedReports/DetailedReports')
);
const Withdraw = lazy(() => import('./containers/Referal/Withdraw'));
const Charity = lazy(() => import('./containers/Referal/Charity'));
const Donate = lazy(() => import('./containers/Referal/Donate'));
const CharityApproval = lazy(() =>
    import('./containers/Referal/CharityApproval')
);
const CharityEdit = lazy(() => import('./containers/Referal/CharityEdit'));
const Notifications = lazy(() =>
    import('./containers/Dashboard/Notifications/Notifications')
);
const PracticeManagement = lazy(() =>
    import('./containers/PracticeManagementGroups/PracticeManagement')
);
const PracticeManagementPMG = lazy(() =>
    import(
        './containers/PracticeManagementGroups/PracticeManagementPMG/PracticeManagementPMG'
    )
);
const PracticeManagementGroupsAdmin = lazy(() =>
    import('./containers/PracticeManagementGroups/PracticeManagementAdmin')
);
const CorporateGroupManagementAdmin = lazy(() =>
    import(
        './containers/CorporateGroupManagement/CorporateGroupManagementAdmin'
    )
);
const UserHome = lazy(() => import('./containers/UserHome/UserHome'));
const PracticeBrokerageServices = lazy(() =>
    import('./containers/PracticeBrokerageServices/PracticeBrokerageServices')
);
const PracticeValuationReports = lazy(() =>
    import('./containers/PracticeValuationReports/PracticeValuationReports')
);
const MyGroupsAdminComponant = lazy(() =>
    import('./containers/MyGroups/MyGroups')
);
const MaintenanceTool = lazy(() =>
    import('./containers/Maintenance/MaintenanceTool')
);
const AuditLogs = lazy(() => import('./containers/AuditLogs/AuditLogsHost'));

const App = () => {
    const dispatch = useDispatch();
    const userActivityEvents = ['keydown', 'mousedown', 'click', 'scroll'];
    const delayToRefreshIfUserInactive = 10000;
    const delayToRefreshIfUserActive = 120000;
    const isOpened = useStateSelector(
        (state) => state.areYouStillHere.isOpened
    );
    const axios = useStateSelector((state) => state.core.axios);
    const location = useLocation();

    useEffect(() => {
        const initiate = async () => {
            await dispatch({
                type: SET_AXIOS,
                payload: createBaseAxiosInstance(),
            });
            if (
                !(
                    location.pathname == '/register' &&
                    location.search.startsWith('?user=')
                )
            ) {
                await dispatch(RefreshNotificationFlags());
            }
            refreshToken();
        };
        initiate();
    }, []);

    useEffect(() => {
        dispatch(FetchServiceFocuses());
    }, [dispatch]);

    const debounce = (func, delay) => {
        let timeoutId;
        return function () {
            const context = this;
            const args = arguments;
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                func.apply(context, args);
            }, delay);
        };
    };

    useEffect(() => {
        let isUserActive = false;
        let refreshTokenTimer;

        const delayedRefreshToken = debounce(() => {
            refreshToken();
        }, delayToRefreshIfUserInactive);

        const activityEventsListener = () => {
            isUserActive = true;
            clearTimeout(refreshTokenTimer);
            refreshTokenTimer = setTimeout(() => {
                isUserActive = false;
                delayedRefreshToken();
            }, delayToRefreshIfUserInactive);
        };

        userActivityEvents.forEach((ev) =>
            document.addEventListener(ev, activityEventsListener, false)
        );

        const intervalId = setInterval(() => {
            if (isUserActive) {
                refreshToken();
            }
        }, delayToRefreshIfUserActive);

        return () => {
            userActivityEvents.forEach((ev) =>
                document.removeEventListener(ev, activityEventsListener, false)
            );
            clearInterval(intervalId);
            clearTimeout(refreshTokenTimer);
        };
    }, [isOpened]);

    const refreshToken = () => {
        if (!isOpened) {
            const refreshToken = localStorage.getItem('refreshToken');
            const deviceKey = localStorage.getItem('deviceKey');
            const accessToken = localStorage.getItem('accessToken');
            const deviceGroupKey = localStorage.getItem('deviceGroupKey');
            const token = localStorage.getItem('token');

            if (refreshToken) {
                axios
                    .post(
                        `/api/auth/refreshUserToken`,
                        {
                            refreshToken: refreshToken,
                            deviceKey: deviceKey,
                            accessToken: accessToken,
                            deviceGroupKey: deviceGroupKey,
                        },
                        {
                            headers: {
                                'Content-Type': 'application/json',
                                Authorization: 'Bearer ' + token,
                            },
                        }
                    )
                    .then((response) => {
                        const authenticationResult = response?.data?.data;
                        if (authenticationResult) {
                            TokenHelper.setTokenToLocalStorage(
                                authenticationResult
                            );
                            let expirationDate = new Date();
                            expirationDate.setSeconds(
                                expirationDate.getSeconds() +
                                    authenticationResult.expiresIn
                            );
                            dispatch(setExpirationDate(expirationDate));
                        }
                        dispatch(setIsOpened(false));
                    });
            }
        }
    };

    const isUserAuth = () => {
        const accessToken = localStorage.getItem('token');
        const userSummary = localStorage.getItem('loggedInUserSummary');
        return accessToken && userSummary;
    };

    function IsUserAuthenticated(component) {
        if (isUserAuth()) {
            return component;
        }
        const returnUrl = location.pathname + location.search;
        const unauthorizedRoutes = [
            '/login',
            '/forgotpwd',
            '/register',
            '/nda-success',
            '/doc-success',
            '/doc-nda-success',
            '/thank-you',
            '/doc-declined',
            '/landing',
            '/',
        ];
        if (
            returnUrl &&
            location.pathname == '/register' &&
            location.search.startsWith('?user=')
        ) {
            return component;
        } else if (
            returnUrl &&
            !unauthorizedRoutes.includes(returnUrl) &&
            !unauthorizedRoutes.includes(location.pathname)
        ) {
            dispatch(setReturnUrl(returnUrl));
        }
        return <Navigate to="/login"></Navigate>;
    }

    return (
        //TODO:- Need to remove multiple suspense if not required
        // ALso These are beta features and it might break in future release
        <Fragment>
            <DefaultMeta />
            <NotificationFlagsProvider />
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/my-referrals"
                        element={IsUserAuthenticated(<MyReferals />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/withdraw"
                        element={IsUserAuthenticated(<Withdraw type="user" />)}
                    />
                </Routes>
            </Suspense>

            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/charities/:id/withdraw"
                        element={IsUserAuthenticated(
                            <Withdraw type="charity" />
                        )}
                    />
                </Routes>
            </Suspense>

            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/data-request-dashboard"
                        element={<DataRequestDashboardPage />}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/data-request-dashboard-built-in"
                        element={
                            <BuiltInModeWrapper>
                                <DataRequestDashboardPage
                                    isBuiltInMode={true}
                                />
                            </BuiltInModeWrapper>
                        }
                    />
                    <Route
                        path="/project-editor-built-in"
                        element={
                            <BuiltInModeWrapper>
                                <ProjectEditorHost />
                            </BuiltInModeWrapper>
                        }
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/data-request-dashboard/others"
                        element={<DataRequestDashboardPage />}
                    />
                </Routes>
            </Suspense>

            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/data-and-document-templates-dashboard"
                        element={
                            <Layout>
                                <DataAndDocumentTemplatesDashboard />
                            </Layout>
                        }
                    />
                    <Route
                        path="/data-and-document-requests-dashboard"
                        element={
                            <Layout>
                                <DataAndDocumentRequestsDashboard />
                            </Layout>
                        }
                    />
                    <Route
                        path="/project-editor"
                        element={
                            <Layout>
                                <ProjectEditorHost />
                            </Layout>
                        }
                    />
                </Routes>
            </Suspense>

            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/donate"
                        element={IsUserAuthenticated(
                            <Donate type="donation" />
                        )}
                    />
                </Routes>
            </Suspense>

            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/nonprofits"
                        element={IsUserAuthenticated(
                            <Donate type="nonprofits" />
                        )}
                    />
                </Routes>
            </Suspense>

            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/charityapproval"
                        element={IsUserAuthenticated(<CharityApproval />)}
                    />
                </Routes>
            </Suspense>

            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/charities/:id/edit"
                        element={IsUserAuthenticated(<CharityEdit />)}
                    />
                </Routes>
            </Suspense>

            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/charities"
                        element={IsUserAuthenticated(<Charity />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route path="/login" element={<Login />} />
                </Routes>
                <Routes>
                    <Route path="/forgotpwd" element={<ForgotPwd />} />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route path="/register" element={<Register />} />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/terms-of-service"
                        element={<TermsOfService />}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/buyer-terms-of-service"
                        element={<BuyerTermsOfService />}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route path="/nda-success" element={<NdaSuccess />} />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/doc-success"
                        element={<DocSuccess type="agreement" />}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/doc-nda-success"
                        element={<DocSuccess type="nda" />}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/doc-signed-frame"
                        element={<DocumentSignedFrame />}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route path="/doc-declined" element={<DocDecline />} />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/report/*"
                        element={IsUserAuthenticated(<Report />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/admin/*"
                        element={IsUserAuthenticated(<Admin />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/dashboard"
                        element={IsUserAuthenticated(<Dashboard />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/maintenance-tool"
                        element={IsUserAuthenticated(<MaintenanceTool />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/ipoc"
                        element={IsUserAuthenticated(
                            <Layout>
                                <PocPage />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route path="/landing" element={<Landing />} />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route path="/F&E" element={<FEPage />} />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/view/freemium/:id"
                        element={IsUserAuthenticated(<FreemiumReportView />)}
                    />
                </Routes>
                <Routes>
                    <Route
                        path="/view/paidevaluation/:id"
                        element={IsUserAuthenticated(<PaidEvaluationReport />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route path="/thank-you" element={<Thankyou />} />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/demos"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/knowledge-library"
                        element={IsUserAuthenticated(
                            <Layout>
                                <KnowledgeBaseContentLibrary></KnowledgeBaseContentLibrary>
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/associates"
                        element={IsUserAuthenticated(
                            <Layout>
                                <Associates></Associates>
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/practice-list"
                        element={IsUserAuthenticated(
                            <Layout>
                                <PracticeList></PracticeList>
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/announcements"
                        element={IsUserAuthenticated(
                            <AnnouncementsTable></AnnouncementsTable>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/profile"
                        element={IsUserAuthenticated(<Profile />)}
                    />
                </Routes>
                <Routes>
                    <Route
                        path="/notifications"
                        element={IsUserAuthenticated(<Notifications />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/referrals"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/practices"
                        element={IsUserAuthenticated(<MyPractices />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/practice/:id"
                        element={IsUserAuthenticated(<Practice />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route exact path="/" element={<Login />} />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/practicerequests"
                        element={IsUserAuthenticated(<PracticeRequests />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/referralrequests"
                        element={IsUserAuthenticated(<ReferralRequests />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">
                                Loading your request...!!!!
                            </h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/detailedreports"
                        element={IsUserAuthenticated(<DetailedReports />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/w-9-Update-Approval"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/payment-request-status"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/new-charity-approval"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/my-resources"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/members"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/discount-code"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/annual-discount"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon></ComingSoon>
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/volume-discount"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/member-services-management"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/subscriptions"
                        element={IsUserAuthenticated(
                            <Layout>
                                <SubscriptionsPage />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/standardization-sevices"
                        element={IsUserAuthenticated(
                            <Layout>
                                <StandardizationServicesPage />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/paidreport-from-updates"
                        element={IsUserAuthenticated(
                            <Layout>
                                <ComingSoon />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/practice-management-groups"
                        element={IsUserAuthenticated(<PracticeManagement />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/practice-brokerage-services/:isComingSoon?"
                        element={IsUserAuthenticated(
                            <PracticeBrokerageServices />
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/practice-valuation-reports"
                        element={IsUserAuthenticated(
                            <PracticeValuationReports />
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/practice-management-pmg/:id"
                        element={IsUserAuthenticated(<PracticeManagementPMG />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/cpoc/:id"
                        element={IsUserAuthenticated(
                            <Layout>
                                <PocPage />
                            </Layout>
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/user-home"
                        element={IsUserAuthenticated(<UserHome />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/pmg-admin"
                        element={IsUserAuthenticated(
                            <PracticeManagementGroupsAdmin />
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/cgm-admin"
                        element={IsUserAuthenticated(
                            <CorporateGroupManagementAdmin />
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/admin-my-groups"
                        element={IsUserAuthenticated(
                            <MyGroupsAdminComponant />
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/audit-logs"
                        element={IsUserAuthenticated(<AuditLogs />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/marketplace/admin"
                        element={IsUserAuthenticated(<AdminMarketplace />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/landscapemap/admin"
                        element={IsUserAuthenticated(<LandscapeMapDemoPage />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/marketplace/engagement/:engagementId"
                        element={IsUserAuthenticated(
                            <ConnectMarketplaceDashboard />
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/marketplace/meetings"
                        element={IsUserAuthenticated(<MeetingLinkRedirect />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/marketplace/admin/engagement/:engagementId"
                        element={IsUserAuthenticated(
                            <ConnectMarketplaceDashboard />
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/opportunities"
                        element={IsUserAuthenticated(<BuyerMarketplace />)}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/marketplace/admin/templates/:id"
                        element={IsUserAuthenticated(
                            <TimelineEditor type="template" />
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/marketplace/admin/engagements/:id"
                        element={IsUserAuthenticated(
                            <TimelineEditor type="engagement" />
                        )}
                    />
                </Routes>
            </Suspense>
            <Suspense
                fallback={
                    <div className="loader-container">
                        <div className="loader-container-inner">
                            <div className="text-center">
                                <Loader type="ball-scale-multiple" />
                            </div>
                            <h6 className="mt-5">Loading your request...</h6>
                        </div>
                    </div>
                }>
                <Routes>
                    <Route
                        path="/marketplace/admin/templates/:id/preview"
                        element={IsUserAuthenticated(
                            <MarketplaceTemplatePreview type="template" />
                        )}
                    />
                </Routes>
            </Suspense>
            <ToastContainer theme="colored" />
        </Fragment>
    );
};

export default App;
